import type { FC, ReactNode } from 'react'

import { useRef } from 'react'

import { cn } from '@/lib/utils'
import { gsap, useGsap, prefersReducedMotion } from '@/lib/gsap'
import { SplitText } from '@/components/generals/split-text'
import { SectionLabel } from '@/components/generals/section-label'
import { FaqIndex } from '@/components/generals/faq-index'
import { Magnetic } from '@/components/generals/magnetic'
import { RollText } from '@/components/generals/roll-link'

import { studioMeta } from '@/constants/site'
import { media } from '@/constants/media'
import { faqs } from '@/constants/studio'

const AVATARS = [
    { id: 'founder', src: media.people.founderAvatar },
    { id: 'second', src: media.people.avatarTwo },
    { id: 'third', src: media.people.avatarThree }
]

/* Same numbered index as the home page, from the same component, so the two
   can never drift. The eyebrow column sticks while the questions pass it and
   the closing block is a ruled band rather than a grey card. */
export const ContactFaq: FC = (): ReactNode => {
    const scope = useRef<HTMLElement>(null)

    useGsap(() => {
        const root = scope.current

        if (!root || prefersReducedMotion()) {
            return
        }

        const block = root.querySelector('[data-more-block]')

        if (!block) {
            return
        }

        /* The closing block arrives as a unit, then the stacked portraits pop
           in one after the other so the overlap reads as a small crowd. */
        gsap.fromTo(block,
            { opacity: 0, y: 40 },
            {
                opacity: 1,
                y: 0,
                duration: 1.05,
                ease: 'noma',
                scrollTrigger: {
                    trigger: block,
                    start: 'top 88%',
                    once: true
                }
            }
        )

        gsap.fromTo(root.querySelectorAll('[data-more-avatar]'),
            { opacity: 0, scale: 0.55, x: 20 },
            {
                opacity: 1,
                scale: 1,
                x: 0,
                duration: 0.75,
                stagger: 0.1,
                delay: 0.25,
                ease: 'back.out(1.8)',
                scrollTrigger: {
                    trigger: block,
                    start: 'top 88%',
                    once: true
                }
            }
        )
    }, scope, [])

    return (
        <section
            id='contact-faq'
            ref={scope}
            className='bg-paper px-5 sec text-ink lg:px-[33px]'
        >
            <div className='mx-auto flex max-w-[1920px] flex-col gap-10 lg:gap-14'>
                <div className='grid gap-8 lg:grid-cols-4 lg:gap-5'>
                    <div className='flex h-fit flex-col gap-6 lg:sticky lg:top-[calc(var(--nav-h)+32px)]'>
                        <SectionLabel label='Answers' />

                        <SplitText
                            as='h2'
                            text='FAQ.'
                            className='t-display text-ink'
                            style={{ fontSize: 'clamp(45px, 6vw, 80px)' }}
                            stagger={0.05}
                        />

                        <p className='t-cond-soft max-w-[28ch] text-[13px] leading-[1.5] text-ash'>
                            Everything worth knowing before the first call.
                        </p>
                    </div>

                    <div className='lg:col-span-3'>
                        <FaqIndex items={faqs} idPrefix='contact-faq' />
                    </div>
                </div>

                <div
                    id='contact-more'
                    data-more-block
                    className='hair-t grid gap-8 pt-9 lg:grid-cols-3 lg:items-start lg:gap-5'
                >
                    <SplitText
                        as='h3'
                        text='more questions?'
                        className='t-display text-ink'
                        style={{ fontSize: 'clamp(30px, 3.2vw, 40px)' }}
                        stagger={0.022}
                    />

                    <div className='flex flex-col gap-5'>
                        <p className='t-cond-soft max-w-[40ch] text-[13px] leading-[1.6] text-ash'>
                            Reach out anytime. We&apos;re happy to answer before you commit to working together.
                        </p>

                        <div className='flex items-center' aria-hidden='true'>
                            {AVATARS.map((avatar, index) => (
                                <img
                                    key={avatar.id}
                                    data-more-avatar
                                    src={avatar.src}
                                    alt=''
                                    loading='lazy'
                                    decoding='async'
                                    className={cn(
                                        'size-10 rounded-full object-cover ring-2 ring-paper',
                                        index > 0 && '-ml-3'
                                    )}
                                />
                            ))}
                        </div>
                    </div>

                    <div className='flex items-start lg:justify-end'>
                        <a
                            href={`mailto:${studioMeta.email}`}
                            data-cursor='link'
                            className='group/roll inline-flex items-center gap-4 text-ink transition-colors duration-300 hover:text-brand'
                        >
                            <RollText
                                label={studioMeta.email}
                                className='t-cond pb-[0.1em] text-[17px] leading-none'
                            />

                            <Magnetic strength={0.25}>
                                <span className='flex size-9 items-center justify-center rounded-[var(--radius-chip)] bg-brand text-paper transition-transform duration-500 group-hover/roll:rotate-45'>
                                    <span data-magnetic-inner className='block'>
                                        <svg
                                            viewBox='0 0 24 24'
                                            className='size-3.5'
                                            fill='none'
                                            aria-hidden='true'
                                            focusable='false'
                                        >
                                            <path d='M5 19L19 5M19 5H8M19 5v11' stroke='currentColor' strokeWidth='2' />
                                        </svg>
                                    </span>
                                </span>
                            </Magnetic>
                        </a>
                    </div>
                </div>
            </div>
        </section>
    )
}
